import { useState, useEffect, useCallback } from 'react';
import { Calendar, Wand2, RefreshCcw, Save, ChevronLeft, ChevronRight, Clock, Check, AlertTriangle, Settings, Sparkles, Edit3, Trash2, Eye, Lock, Unlock } from 'lucide-react';
import ScheduleDayCell from '@/components/admin/ScheduleDayCell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { format, addDays, startOfWeek, addWeeks, subWeeks, isSameDay, isAfter, isBefore } from 'date-fns';

import { API_URL } from '@/config/api';

export default function AdminAutoScheduler() {
  const { token } = useAuth();
  const [currentWeekStart, setCurrentWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [settings, setSettings] = useState({
    is_enabled: false,
    meals_per_day: 5,
    prevent_daily_repeat: true,
    prevent_weekly_repeat: true,
    min_days_between_repeat: 3
  });
  const [generatedSchedule, setGeneratedSchedule] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [existingSchedules, setExistingSchedules] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [applying, setApplying] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayOverride, setDayOverride] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);

  // Get week dates - memoized based on currentWeekStart
  const weekDates = useCallback(() => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      dates.push(addDays(currentWeekStart, i));
    }
    return dates;
  }, [currentWeekStart])();

  // Format dates for API calls - memoized to prevent infinite loops
  const startDateStr = format(currentWeekStart, 'yyyy-MM-dd');
  const endDateStr = format(addDays(currentWeekStart, 6), 'yyyy-MM-dd');

  // Fetch all data - only depends on token and date strings
  const fetchData = useCallback(async () => {
    if (!token) return;
    
    try {
      const [settingsRes, itemsRes, schedulesRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/auto-scheduler/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/menu`),
        fetch(`${API_URL}/api/admin/menu/schedules?start_date=${startDateStr}&end_date=${endDateStr}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      // Handle settings response
      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        setSettings(settingsData);
      }

      // Handle menu items response
      if (itemsRes.ok) {
        const items = await itemsRes.json();
        // Filter to only daily meals that are available
        setMenuItems(items.filter(i => i.menu_type === 'daily_meals' && i.is_available !== false));
      }

      // Handle schedules response
      if (schedulesRes.ok) {
        const schedules = await schedulesRes.json();
        const schedulesMap = {};
        schedules.forEach(s => {
          schedulesMap[s.date] = s;
        });
        setExistingSchedules(schedulesMap);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load scheduler data');
    } finally {
      setLoading(false);
    }
  }, [token, startDateStr, endDateStr]);

  // Effect to fetch data - only when token or date range changes
  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  // Navigate weeks
  const navigateWeek = (direction) => {
    if (direction === 'prev') {
      setCurrentWeekStart(subWeeks(currentWeekStart, 1));
    } else {
      setCurrentWeekStart(addWeeks(currentWeekStart, 1));
    }
    setGeneratedSchedule(null);
  };

  // Generate schedule for the week
  const handleGenerateSchedule = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/auto-scheduler/generate-week`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          start_date: format(currentWeekStart, 'yyyy-MM-dd')
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate schedule');
      }

      const data = await response.json();
      // Map generated_days to schedule format for UI
      const scheduleData = {
        ...data,
        schedule: data.generated_days || data.schedule || []
      };
      setGeneratedSchedule(scheduleData);
      toast.success('Schedule generated! Review and apply when ready.');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setGenerating(false);
    }
  };

  // Apply generated schedule
  const handleApplySchedule = async () => {
    if (!generatedSchedule) return;
    
    setApplying(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/auto-scheduler/apply-generated`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: `Auto-Generated Week ${format(currentWeekStart, 'MMM d, yyyy')}`,
          days: (generatedSchedule.schedule || generatedSchedule.generated_days || []).map(day => ({
            day_of_week: new Date(day.date).getDay(),
            date: day.date,
            meals: (day.meal_details || day.meals || []).map(m => ({
              item_id: m.item_id || m.meal_id,
              name: m.name,
              price: m.price,
              is_available: true
            }))
          })),
          start_date: format(currentWeekStart, 'yyyy-MM-dd'),
          auto_publish: true
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to apply schedule');
      }

      toast.success('Schedule applied successfully!');
      setGeneratedSchedule(null);
      fetchData(); // Refresh existing schedules
    } catch (error) {
      toast.error(error.message);
    } finally {
      setApplying(false);
    }
  };

  // Save settings
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/auto-scheduler/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save settings');
      }

      toast.success('Settings saved successfully!');
      setShowSettings(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingSettings(false);
    }
  };

  // Open day override editor
  const openDayEditor = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const existing = existingSchedules[dateStr];
    const generated = generatedSchedule?.schedule?.find(s => s.date === dateStr);
    
    if (existing) {
      setDayOverride(existing.items || []);
    } else if (generated) {
      setDayOverride(generated.meals || []);
    } else {
      setDayOverride([]);
    }
    setSelectedDay(date);
  };

  // Save day override
  const saveDayOverride = async () => {
    if (!selectedDay) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/menu/schedules`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          date: format(selectedDay, 'yyyy-MM-dd'),
          items: dayOverride.map(item => ({
            item_id: item.item_id || item.meal_id,
            name: item.name,
            price: item.price,
            quantity_available: item.quantity_available || 50
          })),
          settings: {
            pre_order_cutoff: '09:00',
            delivery_start: '12:00',
            delivery_end: '13:00'
          }
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save schedule');
      }

      toast.success('Day schedule saved!');
      setSelectedDay(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Toggle item in day override
  const toggleItemInDay = (item) => {
    const exists = dayOverride.find(i => (i.item_id || i.meal_id) === item.item_id);
    if (exists) {
      setDayOverride(dayOverride.filter(i => (i.item_id || i.meal_id) !== item.item_id));
    } else {
      setDayOverride([...dayOverride, {
        item_id: item.item_id,
        meal_id: item.item_id,
        name: item.name,
        price: item.price,
        quantity_available: 50
      }]);
    }
  };

  // Get schedule for a specific date
  const getScheduleForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const existing = existingSchedules[dateStr];
    const generatedDays = generatedSchedule?.schedule || generatedSchedule?.generated_days || [];
    const generated = generatedDays.find(s => s.date === dateStr);
    
    return {
      existing,
      generated,
      hasExisting: !!existing,
      hasGenerated: !!generated
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-auto-scheduler">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-foreground flex items-center gap-2">
            <Wand2 className="h-6 w-6 text-primary" />
            Auto Menu Scheduler
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Automatically generate and manage weekly menu schedules
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowSettings(true)}
            data-testid="scheduler-settings-btn"
          >
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
          <Button
            onClick={handleGenerateSchedule}
            disabled={generating || menuItems.length === 0}
            className="bg-primary"
            data-testid="generate-schedule-btn"
          >
            {generating ? (
              <>
                <RefreshCcw className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Generate Week
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Auto-Scheduler</p>
                <p className="text-lg font-semibold">{settings.is_enabled ? 'Enabled' : 'Disabled'}</p>
              </div>
              <div className={`p-2 rounded-full ${settings.is_enabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}>
                {settings.is_enabled ? <Unlock className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Available Meals</p>
                <p className="text-lg font-semibold">{menuItems.length} items</p>
              </div>
              <div className="p-2 rounded-full bg-blue-100 text-blue-600">
                <Calendar className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Meals per Day</p>
                <p className="text-lg font-semibold">{settings.meals_per_day} meals</p>
              </div>
              <div className="p-2 rounded-full bg-purple-100 text-purple-600">
                <Clock className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Generated Schedule Preview */}
      {generatedSchedule && (
        <Card className="border-primary/50 bg-primary/5">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-primary">
                  <Sparkles className="h-5 w-5" />
                  Generated Schedule Preview
                </CardTitle>
                <CardDescription>Review the auto-generated schedule before applying</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setGeneratedSchedule(null)}>
                  Discard
                </Button>
                <Button onClick={handleApplySchedule} disabled={applying} className="bg-primary">
                  {applying ? (
                    <>
                      <RefreshCcw className="h-4 w-4 mr-2 animate-spin" />
                      Applying...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Apply Schedule
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>
      )}

      {/* Week Navigation */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigateWeek('prev')}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <h2 className="font-semibold text-lg">
              {format(weekDates[0], 'MMM d')} - {format(weekDates[6], 'MMM d, yyyy')}
            </h2>
            <Button variant="ghost" onClick={() => navigateWeek('next')}>
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Week Grid - uses ScheduleDayCell sub-component */}
          <div className="grid grid-cols-7 gap-2">
            {weekDates.map((date) => (
              <ScheduleDayCell
                key={format(date, 'yyyy-MM-dd')}
                date={date}
                getScheduleForDate={getScheduleForDate}
                onEdit={openDayEditor}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-primary" />
              Scheduler Settings
            </DialogTitle>
            <DialogDescription>
              Configure how the auto-scheduler generates menus
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Enable Auto-Scheduler</Label>
                <p className="text-xs text-muted-foreground">
                  Automatically generate menus for upcoming days
                </p>
              </div>
              <Switch
                checked={settings.is_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, is_enabled: checked })}
              />
            </div>

            {/* Meals per day */}
            <div>
              <Label>Meals per Day</Label>
              <Select
                value={String(settings.meals_per_day)}
                onValueChange={(value) => setSettings({ ...settings, meals_per_day: parseInt(value) })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[3, 4, 5, 6, 7, 8].map(num => (
                    <SelectItem key={num} value={String(num)}>{num} meals</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Prevent daily repeat */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Prevent Daily Repeats</Label>
                <p className="text-xs text-muted-foreground">
                  Don't repeat meals on consecutive days
                </p>
              </div>
              <Switch
                checked={settings.prevent_daily_repeat}
                onCheckedChange={(checked) => setSettings({ ...settings, prevent_daily_repeat: checked })}
              />
            </div>

            {/* Prevent weekly repeat */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Prevent Weekly Repeats</Label>
                <p className="text-xs text-muted-foreground">
                  Don't repeat meals within the same week
                </p>
              </div>
              <Switch
                checked={settings.prevent_weekly_repeat}
                onCheckedChange={(checked) => setSettings({ ...settings, prevent_weekly_repeat: checked })}
              />
            </div>

            {/* Min days between repeat */}
            <div>
              <Label>Minimum Days Between Repeats</Label>
              <Input
                type="number"
                min={1}
                max={14}
                value={settings.min_days_between_repeat}
                onChange={(e) => setSettings({ ...settings, min_days_between_repeat: parseInt(e.target.value) || 3 })}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Minimum days before a meal can appear again
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
            <Button onClick={handleSaveSettings} disabled={savingSettings}>
              {savingSettings ? 'Saving...' : 'Save Settings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Day Override Dialog */}
      <Dialog open={!!selectedDay} onOpenChange={() => setSelectedDay(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              {selectedDay && format(selectedDay, 'EEEE, MMMM d, yyyy')}
            </DialogTitle>
            <DialogDescription>
              Select meals for this day's menu
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {/* Selected count */}
            <div className="mb-4 p-3 bg-muted rounded-lg flex items-center justify-between">
              <span className="text-sm">
                <strong>{dayOverride.length}</strong> meals selected
              </span>
              {dayOverride.length > 0 && (
                <Button variant="ghost" size="sm" onClick={() => setDayOverride([])}>
                  Clear All
                </Button>
              )}
            </div>

            {/* Available meals */}
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {menuItems.map(item => {
                const isSelected = dayOverride.find(i => (i.item_id || i.meal_id) === item.item_id);
                return (
                  <div
                    key={item.item_id}
                    className={`
                      p-3 rounded-lg border cursor-pointer transition-all
                      ${isSelected 
                        ? 'border-primary bg-primary/5 ring-1 ring-primary' 
                        : 'border-border hover:border-primary/50'
                      }
                    `}
                    onClick={() => toggleItemInDay(item)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`
                          w-5 h-5 rounded border-2 flex items-center justify-center
                          ${isSelected ? 'border-primary bg-primary text-white' : 'border-muted-foreground'}
                        `}>
                          {isSelected && <Check className="h-3 w-3" />}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{item.name}</p>
                          <p className="text-xs text-muted-foreground">R{item.price?.toFixed(2)}</p>
                        </div>
                      </div>
                      {item.image && (
                        <img 
                          src={item.image} 
                          alt={item.name} 
                          className="w-12 h-12 rounded object-cover"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedDay(null)}>Cancel</Button>
            <Button onClick={saveDayOverride} disabled={dayOverride.length === 0}>
              <Save className="h-4 w-4 mr-2" />
              Save Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
